import { parseMetafield, ParsedMetafields } from '@shopify/hydrogen-react';
import { Rating } from '@shopify/hydrogen-react/parse-metafield';
import { ProductFilter } from '@shopify/hydrogen-react/storefront-api-types';
import { INCLUDE_EXCLUSIVE_FILTER } from './exclusiveProducts';

export enum ORDER_FULFILLMENT_STATUSES {
  ATTEMPTED_DELIVERY = 'Attempted delivery',
  CANCELED = 'Canceled',
  CONFIRMED = 'Confirmed',
  DELIVERED = 'Delivered',
  FAILURE = 'Failure',
  FULFILLED = 'Fulfilled',
  IN_PROGRESS = 'In Progress',
  IN_TRANSIT = 'In transit',
  LABEL_PRINTED = 'Label printed',
  LABEL_PURCHASED = 'Label purchased',
  LABEL_VOIDED = 'Label voided',
  MARKED_AS_FULFILLED = 'Marked as fulfilled',
  NOT_DELIVERED = 'Not delivered',
  ON_HOLD = 'On Hold',
  OPEN = 'Open',
  OUT_FOR_DELIVERY = 'Out for delivery',
  PARTIALLY_FULFILLED = 'Partially Fulfilled',
  PENDING_FULFILLMENT = 'Pending',
  PICKED_UP = 'Displayed as Picked up',
  READY_FOR_PICKUP = 'Ready for pickup',
  RESTOCKED = 'Restocked',
  REQUEST_DECLINED = 'Request declined',
  SCHEDULED = 'Scheduled',
  SUBMITTED = 'Submitted',
  UNFULFILLED = 'Unfulfilled',
}

export enum ORDER_PAYMENT_STATUSES {
  AUTHORIZED = 'Authorized',
  PAID = 'Paid',
  PARTIALLY_PAID = 'Partially paid',
  PARTIALLY_REFUNDED = 'Partially refunded',
  PENDING = 'Pending',
  REFUNDED = 'Refunded',
  VOIDED = 'Voided',
}

export enum ORDER_DELIVERY_STATUSES {
  AUTHORIZED = 'Authorized',
  PAID = 'Paid',
  PARTIALLY_PAID = 'Partially paid',
  PARTIALLY_REFUNDED = 'Partially refunded',
  PENDING = 'Pending',
  REFUNDED = 'Refunded',
  VOIDED = 'Voided',
}

export const ALL_PRODUCTS_HANDLE = 'all-products';

export enum FEATURED_PRODUCT_ITEM_TYPE {
  TAG = 'tag',
  EXCLUSIVE = 'exclusive',
}

export type FeaturedProductItem = {
  label: string;
  value: string;
  type: FEATURED_PRODUCT_ITEM_TYPE;
};

export const getFeaturedItemFilter: Record<
  FEATURED_PRODUCT_ITEM_TYPE,
  (item: FeaturedProductItem) => ProductFilter
> = {
  [FEATURED_PRODUCT_ITEM_TYPE.EXCLUSIVE]: () => INCLUDE_EXCLUSIVE_FILTER,
  [FEATURED_PRODUCT_ITEM_TYPE.TAG]: item => ({
    tag: item.value,
  }),
};

export const getFeaturedItemQuery: Record<
  FEATURED_PRODUCT_ITEM_TYPE,
  (item: FeaturedProductItem) => string
> = {
  [FEATURED_PRODUCT_ITEM_TYPE.EXCLUSIVE]: () => 'productMetafield.custom.exclusive=true',
  [FEATURED_PRODUCT_ITEM_TYPE.TAG]: item => 'tag=' + item.value,
};

export const getProductItemFilter = (item: FeaturedProductItem): ProductFilter => {
  return getFeaturedItemFilter[item.type] ? getFeaturedItemFilter[item.type](item) : {};
};

export const getProductItemQuery = (item: FeaturedProductItem): string => {
  return getFeaturedItemQuery[item.type] ? getFeaturedItemQuery[item.type](item) : '';
};

export function fulfillmentStatusMessage(status: string) {
  try {
    return ORDER_FULFILLMENT_STATUSES?.[status];
  } catch {
    return status;
  }
}

export function deliveryStatusMessage(status: string) {
  try {
    return ORDER_DELIVERY_STATUSES?.[status];
  } catch {
    return status;
  }
}

export function paymentStatusMessage(status: string) {
  try {
    return ORDER_PAYMENT_STATUSES?.[status];
  } catch {
    return status;
  }
}

export function prepareIds(ids: string[]) {
  return ids.map(id => {
    return btoa(id);
  });
}

export function getReviewsData(product: any): Rating | null {
  const productReviews = product.reviews
    ? parseMetafield<ParsedMetafields['rating']>(product.reviews)
    : null;

  if (!productReviews) {
    return null;
  }

  return {
    scale_max: productReviews.parsedValue ? productReviews.parsedValue.scale_max : 5,
    scale_min: productReviews.parsedValue ? productReviews.parsedValue.scale_min : 1,
    value: productReviews.parsedValue ? productReviews.parsedValue.value : 0,
  };
}

export function generateCheckoutUrl(originalCheckoutUrl: string): string {
  let originalUrl = process.env.REACT_APP_SHOPIFY_STORE_ORIGINAL_CHECKOUT_URL ?? '';
  let dedicatedUrl = process.env.REACT_APP_SHOPIFY_STORE_DEDICATED_CHECKOUT_URL ?? '';

  return !dedicatedUrl.length || !originalCheckoutUrl.includes(originalUrl)
    ? originalCheckoutUrl
    : originalCheckoutUrl.replace(originalUrl, dedicatedUrl);
}

export function extractUrlLastPart(url: string): string {
  return url.substring(url.lastIndexOf('/') + 1);
}

export function extractNumericId(url: string): string {
  const match = url.match(/(\d+)$/);
  return match ? match[0] : '';
}
